import React, { useMemo } from "react";
import { IPairing, IPairingEventGroup, IPairingLogicModifier } from "@vmosolutions/oc-interfaces";
import { Grid } from "@mui/material";
import { useSelector } from "react-redux";
import _ from "lodash";
import { initialValues, LogicModifierForm } from "../Forms/LogicModifierForm";
import { IEmbeddedFormState } from "../PairingEditorWindow";
import moment from "moment-timezone";
import { IRootState } from "src/redux/reducers/CombinedReducers";

export interface IEventGroupOptions {
  onRest: boolean;
  start?: number | null;
  end?: number | null;
  dutyTime: number | null;
  value: string;
  label: string;
  containsOnDutyRest?: boolean;
}
interface ILogicModifiersTabProps {
  id: string;
  pairingId: string;
  className: string;
  updateLogicModifier: (logicModifier: IPairingLogicModifier, isNew: boolean) => void;
  logicModifier?: IPairingLogicModifier;
  setSelectedLogicModifier: (pairingLM: IPairingLogicModifier | null) => void;
  setEmbeddedFormStates: React.Dispatch<React.SetStateAction<IEmbeddedFormState>>;
  isUTC?: boolean;
}

const getMMMDo = (time: number, tz: string): string => {
  return moment.tz(time, tz).format("MMM Do");
};

const getEventGroupDateLabel = (start: number, startTZ: string, end: number, endTZ: string, isUTC: boolean): string => {
  return `[${getMMMDo(start, isUTC ? "" : startTZ)} - ${getMMMDo(end, isUTC ? "" : endTZ)}]`;
};

export const LogicModifiersTab = (props: ILogicModifiersTabProps): JSX.Element => {
  const {
    id,
    pairingId,
    className,
    updateLogicModifier,
    logicModifier,
    setSelectedLogicModifier,
    setEmbeddedFormStates,
    isUTC,
  } = props;
  const storedPairing = useSelector((state: IRootState) => state.crewSchedule.pairingEditors[pairingId]);
  const mosaicPane = useMemo(() => {
    return id ? document.getElementById(`${id}-panel`) : null;
  }, [id]);

  //create event group options
  const eventGroupOptions: IEventGroupOptions[] = useMemo(() => {
    //do we know if the date is in UTC or LOCAL
    return Array.isArray(storedPairing?.eventGroups)
      ? storedPairing?.eventGroups.map((eventGroup: IPairingEventGroup, index: number) => ({
          onRest: eventGroup.restPeriods.length > 0,
          start: eventGroup.start?.valueOf(),
          end: eventGroup.end?.valueOf(),
          dutyTime: (eventGroup.start?.valueOf() + eventGroup.end?.valueOf()) / 2,
          value: (index + 1).toString(),
          containsOnDutyRest: eventGroup.nonFlyingAssignments.some(
            (assignment) => assignment.assignmentType.isOnDutyRest
          ),
          label: eventGroup.restPeriods.length
            ? `Rest: in ${eventGroup.origin} ${getEventGroupDateLabel(
                eventGroup.start?.valueOf(),
                eventGroup.originTimezoneId,
                eventGroup.end?.valueOf(),
                eventGroup.destinationTimezoneId,
                isUTC
              )}`
            : `Duty Period: ${eventGroup.origin} - ${eventGroup.destination} ${
                eventGroup.origin
              } ${getEventGroupDateLabel(
                eventGroup.start?.valueOf(),
                eventGroup.originTimezoneId,
                eventGroup.end?.valueOf(),
                eventGroup.destinationTimezoneId,
                isUTC
              )}`,
        }))
      : [];
  }, [storedPairing?.eventGroups, isUTC]);

  return (
    <Grid container direction="column" className={className}>
      <Grid item>
        {eventGroupOptions?.length > 0 && (
          <LogicModifierForm
            mosaicPane={mosaicPane}
            logicModifier={logicModifier ?? initialValues}
            setSelectedLogicModifier={setSelectedLogicModifier}
            updateLogicModifier={updateLogicModifier}
            eventGroupOptions={eventGroupOptions}
            setEmbeddedFormStates={setEmbeddedFormStates}
            isPairingEditor={true}
          />
        )}
      </Grid>
    </Grid>
  );
};
